import { useEffect, useMemo, useRef, useState } from "react";

import { PlanningCommentsPanel } from "@/components/PlanningCommentsPanel";
import { useConfirm } from "@/components/ConfirmDialog";
import { PlanningCompactView } from "@/components/PlanningCompactView";
import { PlanningDayTracks, type BlockAction } from "@/components/PlanningDayTracks";
import { SlotKindChip } from "@/components/SlotKindChip";
import { useToast } from "@/components/ToastProvider";

import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PublishIcon from "@mui/icons-material/Publish";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listExerciseModes, listSlotTemplates, updateSlotTemplate } from "@/api/catalog";
import { listOrganizations } from "@/api/organizations";
import {
  createAssignment,
  deleteAssignment,
  generatePlanning,
  getPlanning,
  publishPlanning,
  type GenerateReport,
} from "@/api/planning";
import { listPractitioners } from "@/api/practitioners";
import { applyShiftPatterns } from "@/api/shift_patterns";
import type {
  Assignment,
  ExerciseMode,
  Organization,
  Practitioner,
  SlotTemplate,
} from "@/api/types";
import { usePermission } from "@/auth/usePermission";
import { slotIsOperating } from "@/lib/operatingHours";
import { timeToMinutes } from "@/lib/dayBlocks";
import { useAuthStore } from "@/store/authStore";
import { AssignmentDialog } from "@/components/AssignmentDialog";
import { AssignmentEditDialog } from "@/components/AssignmentEditDialog";
import { CloseMonthDialog } from "@/components/CloseMonthDialog";
import { BulkPublishDialog } from "@/components/BulkPublishDialog";
import { ExplainGapsDialog } from "@/components/ExplainGapsDialog";
import { GenerateOptionsDialog, type GenerateOptionsResult } from "@/components/GenerateOptionsDialog";
import { GenerateReportDialog } from "@/components/GenerateReportDialog";
import { PageHeader } from "@/components/PageHeader";
import { PlanBlancAdminButton } from "@/components/PlanBlancAdminButton";

function monthLabel(year: number, month: number, locale: string): string {
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString(locale, { month: "long", year: "numeric" });
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function ymd(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function coverageForDate(
  date: string,
  slots: SlotTemplate[],
  assignmentsBySlot: Map<string, Assignment[]>,
  organization: Organization | null = null,
): { coveredHours: number; totalHours: number; pct: number } {
  let coveredMin = 0;
  let totalMin = 0;
  for (const s of slots) {
    if (!slotIsOperating(organization, date, s)) continue;
    const dur = timeToMinutes(s.end_time) - timeToMinutes(s.start_time);
    totalMin += dur;
    const list = assignmentsBySlot.get(`${date}__${s.id}`) ?? [];
    if (list.some((a) => !a.cancelled_at)) coveredMin += dur;
  }
  return {
    coveredHours: Math.round((coveredMin / 60) * 10) / 10,
    totalHours: Math.round((totalMin / 60) * 10) / 10,
    pct: totalMin > 0 ? Math.round((coveredMin / totalMin) * 100) : 0,
  };
}

function coverageColor(pct: number): "success" | "warning" | "error" {
  if (pct >= 80) return "success";
  if (pct >= 50) return "warning";
  return "error";
}

function dayOfWeek(year: number, month: number, day: number): number {
  // 1=Mon..7=Sun (ISO)
  const js = new Date(year, month - 1, day).getDay();
  return js === 0 ? 7 : js;
}

type CellTarget = {
  date: string;
  slot: SlotTemplate;
};

export function PlanningPage() {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.me);

  const canEdit = usePermission("planning:edit");
  const canPublish = usePermission("planning:publish");

  // Org selection — default to active org from /me
  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 100 }),
  });

  const [orgId, setOrgId] = useState<string>("");
  useEffect(() => {
    if (!orgId && orgs.data?.items.length) {
      const preferred = me?.active_org_id
        && orgs.data.items.find((o) => o.id === me.active_org_id)
        ? me.active_org_id
        : orgs.data.items[0]?.id ?? "";
      setOrgId(preferred);
    }
  }, [orgs.data, orgId, me?.active_org_id]);

  const currentOrg = useMemo(
    () => (orgs.data?.items ?? []).find((o) => o.id === orgId) ?? null,
    [orgs.data, orgId],
  );

  // Month selection
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  function navMonth(delta: number): void {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  }
  function goToday(): void {
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  }

  // Slot templates + planning + practitioners + modes
  const slots = useQuery({
    queryKey: ["slot-templates", orgId, true],
    queryFn: () => (orgId ? listSlotTemplates(orgId, true) : Promise.resolve([])),
    enabled: Boolean(orgId),
  });

  const planning = useQuery({
    queryKey: ["planning", orgId, year, month],
    queryFn: () => getPlanning(orgId, year, month),
    enabled: Boolean(orgId),
  });

  const practitioners = useQuery({
    queryKey: ["practitioners-active-300"],
    queryFn: () => listPractitioners({ status: "ACTIVE", limit: 200 }),
  });

  const modes = useQuery({
    queryKey: ["exercise-modes-active"],
    queryFn: () => listExerciseModes(true),
  });

  // Lookups
  const practitionerById = useMemo(() => {
    const m = new Map<string, Practitioner>();
    for (const p of practitioners.data?.items ?? []) m.set(p.id, p);
    return m;
  }, [practitioners.data]);
  const modeById = useMemo(() => {
    const m = new Map<string, ExerciseMode>();
    for (const e of modes.data ?? []) m.set(e.id, e);
    return m;
  }, [modes.data]);

  // Pending-ops buffer — drag/resize/duplicate accumulate here, then a single
  // "Save" button POST/DELETEs them. Discarding wipes the buffer with no
  // server-side changes.
  type PendingOp =
    | {
        kind: "create";
        clientId: string;
        date: string;
        slot_template_id: string;
        practitioner_id: string;
        exercise_mode_id: string;
      }
    | { kind: "delete"; clientId: string; assignment_id: string };
  const [pendingOps, setPendingOps] = useState<PendingOp[]>([]);

  // Group assignments by (date, slot_template_id), applying pending ops
  const assignmentsBySlot = useMemo(() => {
    const deletedIds = new Set(
      pendingOps.flatMap((o) => (o.kind === "delete" ? [o.assignment_id] : [])),
    );
    const m = new Map<string, Assignment[]>();
    for (const a of planning.data?.assignments ?? []) {
      if (deletedIds.has(a.id)) continue;
      const key = `${a.date}__${a.slot_template_id}`;
      const list = m.get(key) ?? [];
      list.push(a);
      m.set(key, list);
    }
    // Inject creates as synthetic assignments
    for (const o of pendingOps) {
      if (o.kind !== "create") continue;
      const key = `${o.date}__${o.slot_template_id}`;
      const list = m.get(key) ?? [];
      list.push({
        id: o.clientId,
        date: o.date,
        slot_template_id: o.slot_template_id,
        practitioner_id: o.practitioner_id,
        exercise_mode_id: o.exercise_mode_id,
        planning_id: planning.data?.planning.id ?? "",
        source: "MANUAL",
        comment: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as unknown as Assignment);
      m.set(key, list);
    }
    return m;
  }, [planning.data, pendingOps]);

  // Dialog
  const [cellTarget, setCellTarget] = useState<CellTarget | null>(null);
  /** Shift currently open in the correction (prévisionnel → réalisé) dialog. */
  const [editAssignment, setEditAssignment] = useState<Assignment | null>(null);
  const [closeMonthOpen, setCloseMonthOpen] = useState(false);

  // Planning clipboard — a copied assignment's identity, pasteable onto any
  // cell. Deliberately not the system clipboard: this is structured data, and
  // reading the OS clipboard would need a permission prompt for no gain.
  const [clipboard, setClipboard] = useState<{
    practitionerId: string;
    exerciseModeId: string;
    label: string;
  } | null>(null);

  // View mode: scope (month/week) × detail level (detail/compact)
  const [scope, setScope] = useState<"month" | "week">("month");
  const [detail, setDetail] = useState<"detail" | "compact">("detail");
  // Week anchor: monday of the week we're viewing (defaults to first monday of month)
  const [weekAnchor, setWeekAnchor] = useState<Date>(() => {
    const first = new Date(year, month - 1, 1);
    const offset = (first.getDay() + 6) % 7;
    first.setDate(first.getDate() - offset);
    return first;
  });
  useMemo(() => {
    // Re-anchor when month changes
    const first = new Date(year, month - 1, 1);
    const offset = (first.getDay() + 6) % 7;
    first.setDate(first.getDate() - offset);
    setWeekAnchor(first);
  }, [year, month]);

  const clipboardRef = useRef<{
    practitionerId: string;
    exerciseModeId: string;
    label: string;
  } | null>(null);
  useEffect(() => {
    clipboardRef.current = clipboard;
  }, [clipboard]);

  // Listen for block-action events from PlanningDayTracks (detail grid).
  // The track component is rendered deep inside the grid, so a window event
  // is the lightest way to thread the handler without N props.
  useEffect(() => {
    const handler = (e: Event) => {
      const action = (e as CustomEvent).detail as
        | {
            kind:
              | "edit"
              | "move"
              | "duplicate"
              | "delete"
              | "resize"
              | "copy"
              | "paste";
            assignments: Assignment[];
            newDate?: string;
            newSlotIds?: string[];
            practitionerId?: string;
            exerciseModeId?: string;
            date?: string;
            // Paste target — the cell the user right-clicked.
            slotId?: string;
          }
        | undefined;
      if (!action) return;
      // Helper: strip pending creates that match the action's assignments by
      // synthetic clientId, and return deletes only for REAL assignments.
      const reconcileRemoval = (
        prev: PendingOp[],
        assignments: Assignment[],
      ): { kept: PendingOp[]; deletes: PendingOp[] } => {
        const syntheticIds = new Set(
          assignments.map((a) => a.id).filter((id) => id.startsWith("new-")),
        );
        const realAssignmentIds = assignments
          .map((a) => a.id)
          .filter((id) => !id.startsWith("new-"));
        const kept = prev.filter(
          (o) => !(o.kind === "create" && syntheticIds.has(o.clientId)),
        );
        const deletes: PendingOp[] = realAssignmentIds.map((id) => ({
          kind: "delete" as const,
          clientId: `del-${id}-${Date.now()}`,
          assignment_id: id,
        }));
        return { kept, deletes };
      };
      const uniqueClientId = (prefix: string) =>
        `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

      if (action.kind === "edit") {
        // Correcting a shift hits the API directly rather than joining the
        // pending-ops buffer: the réalisé is an audited statement about the
        // past, not a draft edit of the forecast.
        const target = action.assignments.find((a) => !a.id.startsWith("new-"));
        if (!target) {
          toast(t("planning.editUnsavedBlock"), "warning");
          return;
        }
        setEditAssignment(target);
      } else if (action.kind === "copy") {
        const first = action.assignments[0];
        if (!first) return;
        const prac = practitionerById.get(first.practitioner_id);
        setClipboard({
          practitionerId: first.practitioner_id,
          exerciseModeId: first.exercise_mode_id,
          label: prac?.display_name ?? first.practitioner_id.slice(0, 8),
        });
        toast(
          t("planning.clipboardCopied", {
            name: prac?.display_name ?? "",
          }),
          "info",
        );
      } else if (action.kind === "paste") {
        const cb = clipboardRef.current;
        if (!cb || !action.slotId || !action.date) return;
        setPendingOps((prev) => [
          ...prev,
          {
            kind: "create" as const,
            clientId: uniqueClientId(`new-${action.slotId}`),
            date: action.date!,
            slot_template_id: action.slotId!,
            practitioner_id: cb.practitionerId,
            exercise_mode_id: cb.exerciseModeId,
          },
        ]);
        toast(t("planning.clipboardPasted", { name: cb.label }), "info");
      } else if (action.kind === "delete") {
        setPendingOps((prev) => {
          const { kept, deletes } = reconcileRemoval(prev, action.assignments);
          return [...kept, ...deletes];
        });
        toast(
          t("planning.bufferDelete", { count: action.assignments.length }),
          "info",
        );
      } else if (
        action.kind === "resize" &&
        action.practitionerId &&
        action.exerciseModeId &&
        action.date &&
        action.newSlotIds
      ) {
        setPendingOps((prev) => {
          const { kept, deletes } = reconcileRemoval(prev, action.assignments);
          const creates: PendingOp[] = action.newSlotIds!.map((sid) => ({
            kind: "create" as const,
            clientId: uniqueClientId(`new-${sid}`),
            date: action.date!,
            slot_template_id: sid,
            practitioner_id: action.practitionerId!,
            exercise_mode_id: action.exerciseModeId!,
          }));
          return [...kept, ...deletes, ...creates];
        });
      } else if ((action.kind === "move" || action.kind === "duplicate") && action.newDate) {
        setPendingOps((prev) => {
          const next = [...prev];
          if (action.kind === "move") {
            const { kept, deletes } = reconcileRemoval(prev, action.assignments);
            next.splice(0, next.length, ...kept, ...deletes);
          }
          const created: PendingOp[] = action.assignments.map((a) => ({
            kind: "create" as const,
            clientId: uniqueClientId(`new-${a.slot_template_id}`),
            date: action.newDate!,
            slot_template_id: a.slot_template_id,
            practitioner_id: a.practitioner_id,
            exercise_mode_id: a.exercise_mode_id,
          }));
          next.push(...created);
          return next;
        });
        toast(
          action.kind === "move"
            ? t("planning.bufferMove", { date: action.newDate })
            : t("planning.bufferDuplicate", { date: action.newDate }),
          "info",
        );
      }
    };
    window.addEventListener("planning-block-action", handler);
    return () => window.removeEventListener("planning-block-action", handler);
  }, [t, toast]);

  const savePendingOps = useMutation({
    mutationFn: async () => {
      if (!orgId || pendingOps.length === 0) return;
      // Deletes first, then creates, to avoid race on (date, slot, practitioner)
      const deletes = pendingOps.filter((o) => o.kind === "delete");
      const creates = pendingOps.filter((o) => o.kind === "create");
      for (const op of deletes) {
        if (op.kind !== "delete") continue;
        await deleteAssignment(orgId, year, month, op.assignment_id);
      }
      for (const op of creates) {
        if (op.kind !== "create") continue;
        await createAssignment(orgId, year, month, {
          date: op.date,
          slot_template_id: op.slot_template_id,
          practitioner_id: op.practitioner_id,
          exercise_mode_id: op.exercise_mode_id,
        });
      }
    },
    onSuccess: () => {
      setPendingOps([]);
      qc.invalidateQueries({ queryKey: ["planning", orgId, year, month] });
    },
  });

  // Slot template header selection (right-click → set target headcount)
  const [selectedSlotIds, setSelectedSlotIds] = useState<Set<string>>(new Set());
  const [slotMenu, setSlotMenu] = useState<{ x: number; y: number; slotId: string } | null>(null);
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [targetValue, setTargetValue] = useState(1);

  const slotTargetMutation = useMutation({
    mutationFn: async (vals: { ids: string[]; target: number }) => {
      for (const id of vals.ids) {
        await updateSlotTemplate(id, { target_headcount: vals.target });
      }
      return vals.ids.length;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["slot-templates", orgId] });
      setTargetDialogOpen(false);
      setSelectedSlotIds(new Set());
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAssignment(orgId, year, month, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["planning", orgId, year, month] }),
  });

  const publishMutation = useMutation({
    mutationFn: () => publishPlanning(orgId, year, month),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["planning", orgId, year, month] }),
  });

  const [generateReport, setGenerateReport] = useState<GenerateReport | null>(null);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [explainOpen, setExplainOpen] = useState(false);
  const [bulkPublishOpen, setBulkPublishOpen] = useState(false);
  const applyPatternsMutation = useMutation({
    mutationFn: () => applyShiftPatterns(orgId, year, month),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["planning", orgId, year, month] });
      toast(
        t("shiftPatterns.applyResult", {
          created: r.created,
          skipped: r.skipped_conflicts,
        }),
        "success",
      );
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      toast(typeof detail === "string" ? detail : (e as Error).message, "error");
    },
  });
  const generateMutation = useMutation({
    mutationFn: (opts: GenerateOptionsResult) =>
      generatePlanning(orgId, year, month, { time_limit_seconds: 30, ...opts }),
    onSuccess: (report) => {
      setGenerateOpen(false);
      setGenerateReport(report);
      qc.invalidateQueries({ queryKey: ["planning", orgId, year, month] });
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      toast(typeof detail === "string" ? detail : (e as Error).message, "error");
    },
  });

  const slotList = useMemo(
    () => (slots.data ?? []).filter((s) => s.is_active),
    [slots.data],
  );

  // Who is already busy on each slot of the targeted day — lets the assignment
  // dialog filter candidates over a multi-hour range, not just the clicked cell.
  const occupiedBySlotForTarget = useMemo(() => {
    if (!cellTarget) return undefined;
    return new Map(
      slotList.map((s) => [
        s.id,
        new Set(
          (assignmentsBySlot.get(`${cellTarget.date}__${s.id}`) ?? []).map(
            (a) => a.practitioner_id,
          ),
        ),
      ]),
    );
  }, [cellTarget, slotList, assignmentsBySlot]);

  const days = orgId ? daysInMonth(year, month) : 0;
  const monthDays = useMemo(
    () => Array.from({ length: days }, (_, i) => i + 1),
    [days],
  );

  // Days actually rendered depend on scope.
  // NOTE: keep every hook above the early returns below — a hook called after
  // them changes the hook count between renders (React error #310).
  const dayList = useMemo(() => {
    if (scope === "month") return monthDays;
    // Week scope: 7 days starting at weekAnchor, but only those that fall in
    // the current (year, month) — others are blanked by the grid.
    const out: number[] = [];
    const cur = new Date(weekAnchor);
    for (let i = 0; i < 7; i++) {
      if (cur.getFullYear() === year && cur.getMonth() + 1 === month) {
        out.push(cur.getDate());
      }
      cur.setDate(cur.getDate() + 1);
    }
    return out.length > 0 ? out : monthDays.slice(0, 7);
  }, [scope, weekAnchor, year, month, monthDays]);

  if (orgs.isLoading) return <CircularProgress />;
  if ((orgs.data?.items.length ?? 0) === 0) {
    return (
      <Box>
        <PageHeader title={t("planning.title")} subtitle={t("planning.subtitle")} helpTerm="planning" />
        <Alert severity="warning">{t("planning.noOrgAccessible")}</Alert>
      </Box>
    );
  }

  const status = planning.data?.planning.status;
  const statusChip = status === "PUBLISHED"
    ? <Chip color="success" label={t("planning.published")} size="small" />
    : status === "LOCKED"
      ? <Chip color="warning" label={t("planning.locked")} size="small" />
      : <Chip variant="outlined" label={t("planning.draft")} size="small" />;

  const shiftWeek = (delta: number) => {
    const next = new Date(weekAnchor);
    next.setDate(next.getDate() + delta * 7);
    setWeekAnchor(next);
  };

  return (
    <Box>
      <PageHeader
        title={t("planning.title")}
        subtitle={t("planning.subtitle")}
        actions={
          <>
            {pendingOps.length > 0 && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={() => savePendingOps.mutate()}
                  disabled={savePendingOps.isPending}
                >
                  {t("planning.savePending", { count: pendingOps.length })}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    const ok = await confirm({
                      message: t("planning.discardConfirm", { count: pendingOps.length }),
                      confirmLabel: t("common.confirm"),
                      danger: true,
                    });
                    if (ok) setPendingOps([]);
                  }}
                  disabled={savePendingOps.isPending}
                >
                  {t("planning.discardPending")}
                </Button>
              </>
            )}
            {orgId && <PlanBlancAdminButton organizationId={orgId} />}
            {canEdit && status !== "PUBLISHED" && status !== "LOCKED" && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<EventRepeatIcon />}
                  onClick={async () => {
                    const ok = await confirm({
                      title: t("shiftPatterns.applyTitle", { defaultValue: "Appliquer les trames" }),
                      message: t("shiftPatterns.applyConfirm"),
                      confirmLabel: t("common.confirm"),
                    });
                    if (ok) {
                      applyPatternsMutation.mutate();
                    }
                  }}
                  disabled={applyPatternsMutation.isPending}
                >
                  {applyPatternsMutation.isPending
                    ? t("shiftPatterns.applying")
                    : t("shiftPatterns.applyCta")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setGenerateOpen(true)}
                  disabled={generateMutation.isPending}
                >
                  {generateMutation.isPending ? t("planning.generating") : t("planning.generate")}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HelpOutlineIcon />}
                  onClick={() => setExplainOpen(true)}
                >
                  {t("explainGaps.cta")}
                </Button>
              </>
            )}
            {canPublish && status !== "PUBLISHED" && (
              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                onClick={() => publishMutation.mutate()}
                disabled={publishMutation.isPending}
              >
                {t("planning.publish")}
              </Button>
            )}
            {canPublish && (
              <Button
                variant="outlined"
                startIcon={<PublishIcon />}
                onClick={() => setBulkPublishOpen(true)}
              >
                {t("bulkPublish.cta")}
              </Button>
            )}
            {canEdit && (
              <Button
                variant="outlined"
                color="success"
                startIcon={<FactCheckIcon />}
                onClick={() => setCloseMonthOpen(true)}
              >
                {t("planning.closeMonth")}
              </Button>
            )}
          </>
        }
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("planning.organization")}</InputLabel>
          <Select
            label={t("planning.organization")}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={t("planning.previousMonth")}>
            <IconButton onClick={() => navMonth(-1)}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="h6" sx={{ minWidth: 200, textAlign: "center", textTransform: "capitalize" }}>
            {monthLabel(year, month, i18n.language)}
          </Typography>
          <Tooltip title={t("planning.nextMonth")}>
            <IconButton onClick={() => navMonth(1)}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
          <Button size="small" onClick={goToday}>
            {t("planning.today")}
          </Button>
          {statusChip}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(_, v) => v && setScope(v)}
        >
          <ToggleButton value="month">{t("planning.scopeMonth")}</ToggleButton>
          <ToggleButton value="week">{t("planning.scopeWeek")}</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={detail}
          onChange={(_, v) => v && setDetail(v)}
        >
          <ToggleButton value="detail">{t("planning.detailFull")}</ToggleButton>
          <ToggleButton value="compact">{t("planning.detailCompact")}</ToggleButton>
        </ToggleButtonGroup>
        {scope === "week" && (
          <>
            <IconButton size="small" onClick={() => shiftWeek(-1)}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {weekAnchor.toLocaleDateString(i18n.language, {
                day: "2-digit",
                month: "short",
              })}{" "}
              →{" "}
              {new Date(weekAnchor.getTime() + 6 * 86400000).toLocaleDateString(
                i18n.language,
                { day: "2-digit", month: "short" },
              )}
            </Typography>
            <IconButton size="small" onClick={() => shiftWeek(1)}>
              <ChevronRightIcon />
            </IconButton>
          </>
        )}
      </Stack>

      {planning.isLoading || slots.isLoading ? (
        <CircularProgress />
      ) : slotList.length === 0 ? (
        <Alert severity="info">{t("planning.noSlotTemplates")}</Alert>
      ) : detail === "compact" ? (
        <Paper
          variant="outlined"
          onContextMenu={(e) => e.preventDefault()}
          sx={{ p: 1.5 }}
        >
          <PlanningCompactView
            year={year}
            month={month}
            days={dayList}
            slots={slotList}
            assignmentsBySlot={assignmentsBySlot}
            practitionerById={practitionerById}
            modeById={modeById}
            canEdit={canEdit && status !== "LOCKED"}
            onBlockAction={(action) => {
              // Copy/paste share the page-level clipboard with the detail grid,
              // so a block copied in one view pastes in the other.
              if (action.kind === "copy") {
                const first = action.assignments[0];
                if (!first) return;
                const prac = practitionerById.get(first.practitioner_id);
                const name = prac?.display_name ?? first.practitioner_id.slice(0, 8);
                setClipboard({
                  practitionerId: first.practitioner_id,
                  exerciseModeId: first.exercise_mode_id,
                  label: name,
                });
                toast(t("planning.clipboardCopied", { name }), "info");
              } else if (action.kind === "paste") {
                const cb = clipboardRef.current;
                if (!cb) return;
                setPendingOps((prev) => [
                  ...prev,
                  {
                    kind: "create" as const,
                    clientId: `paste-${action.slotId}-${action.date}-${prev.length}`,
                    date: action.date,
                    slot_template_id: action.slotId,
                    practitioner_id: cb.practitionerId,
                    exercise_mode_id: cb.exerciseModeId,
                  },
                ]);
                toast(t("planning.clipboardPasted", { name: cb.label }), "info");
              } else if (action.kind === "delete") {
                const newOps: PendingOp[] = action.assignments.map((a) => ({
                  kind: "delete" as const,
                  clientId: `del-${a.id}`,
                  assignment_id: a.id,
                }));
                setPendingOps((prev) => [...prev, ...newOps]);
                toast(
                  t("planning.bufferDelete", { count: action.assignments.length }),
                  "info",
                );
              } else {
                // Move = delete + recreate on newDate ; Duplicate = recreate only
                const created: PendingOp[] = action.assignments.map((a) => ({
                  kind: "create" as const,
                  clientId: `new-${a.id}-${action.newDate}`,
                  date: action.newDate,
                  slot_template_id: a.slot_template_id,
                  practitioner_id: a.practitioner_id,
                  exercise_mode_id: a.exercise_mode_id,
                }));
                const deleted: PendingOp[] =
                  action.kind === "move"
                    ? action.assignments.map((a) => ({
                        kind: "delete" as const,
                        clientId: `del-${a.id}`,
                        assignment_id: a.id,
                      }))
                    : [];
                setPendingOps((prev) => [...prev, ...deleted, ...created]);
                toast(
                  action.kind === "move"
                    ? t("planning.bufferMove", { date: action.newDate })
                    : t("planning.bufferDuplicate", { date: action.newDate }),
                  "info",
                );
              }
            }}
          />
        </Paper>
      ) : isMobile ? (
        <PlanningGridMobile
          year={year}
          month={month}
          days={dayList}
          slots={slotList}
          assignmentsBySlot={assignmentsBySlot}
          practitionerById={practitionerById}
          modeById={modeById}
          canEdit={canEdit && status !== "LOCKED"}
          organization={currentOrg}
          clipboardLabel={clipboard?.label ?? null}
          onCellClick={(c) => setCellTarget(c)}
          onDelete={(id) => {
            void (async () => {
              const ok = await confirm({
                message: t("planning.removeConfirm"),
                confirmLabel: t("common.delete"),
                danger: true,
              });
              if (ok) deleteMutation.mutate(id);
            })();
          }}
          t={t}
        />
      ) : (
        <PlanningGridDesktop
          year={year}
          month={month}
          days={dayList}
          slots={slotList}
          assignmentsBySlot={assignmentsBySlot}
          practitionerById={practitionerById}
          modeById={modeById}
          canEdit={canEdit && status !== "LOCKED"}
          organization={currentOrg}
          clipboardLabel={clipboard?.label ?? null}
          onCellClick={(c) => setCellTarget(c)}
          onDelete={(id) => {
            void (async () => {
              const ok = await confirm({
                message: t("planning.removeConfirm"),
                confirmLabel: t("common.delete"),
                danger: true,
              });
              if (ok) deleteMutation.mutate(id);
            })();
          }}
          onHeaderRightClick={(slotId, e) => {
            setSelectedSlotIds((prev) => {
              if (prev.has(slotId)) return prev;
              const next = new Set(prev);
              next.add(slotId);
              return next;
            });
            setSlotMenu({ x: e.clientX + 2, y: e.clientY - 6, slotId });
          }}
          selectedSlotIds={selectedSlotIds}
          onHeaderToggleSelect={(slotId, additive) => {
            setSelectedSlotIds((prev) => {
              const next = additive ? new Set(prev) : new Set<string>();
              if (next.has(slotId)) next.delete(slotId);
              else next.add(slotId);
              return next;
            });
          }}
          t={t}
        />
      )}

      <GenerateOptionsDialog
        open={generateOpen}
        busy={generateMutation.isPending}
        onCancel={() => setGenerateOpen(false)}
        onConfirm={(opts) => generateMutation.mutate(opts)}
      />

      <ExplainGapsDialog
        open={explainOpen}
        organizationId={orgId}
        year={year}
        month={month}
        onClose={() => setExplainOpen(false)}
      />

      <BulkPublishDialog
        open={bulkPublishOpen}
        defaultYear={year}
        defaultMonth={month}
        onClose={() => setBulkPublishOpen(false)}
      />

      <GenerateReportDialog
        open={generateReport !== null}
        report={generateReport}
        onClose={() => setGenerateReport(null)}
      />

      <AssignmentEditDialog
        open={editAssignment !== null}
        onClose={() => setEditAssignment(null)}
        organizationId={orgId}
        year={year}
        month={month}
        assignment={editAssignment}
        slots={slotList}
      />

      <CloseMonthDialog
        open={closeMonthOpen}
        onClose={() => setCloseMonthOpen(false)}
        organizationId={orgId}
        year={year}
        month={month}
      />

      <AssignmentDialog
        open={cellTarget !== null}
        onClose={() => setCellTarget(null)}
        organizationId={orgId}
        year={year}
        month={month}
        date={cellTarget?.date ?? ""}
        slot={cellTarget?.slot ?? null}
        excludedPractitionerIds={
          cellTarget
            ? new Set(
                (assignmentsBySlot.get(`${cellTarget.date}__${cellTarget.slot.id}`) ?? []).map(
                  (a) => a.practitioner_id,
                ),
              )
            : undefined
        }
        daySlots={slotList}
        occupiedBySlot={occupiedBySlotForTarget}
      />

      {planning.data?.planning.id && (
        <Box sx={{ mt: 3 }}>
          <PlanningCommentsPanel planningId={planning.data.planning.id} />
        </Box>
      )}

      <Menu
        open={slotMenu !== null}
        onClose={() => setSlotMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={
          slotMenu ? { top: slotMenu.y, left: slotMenu.x } : undefined
        }
      >
        <MenuItem disabled>
          <Typography variant="caption">
            {t("planning.slotMenuHint", { count: selectedSlotIds.size })}
          </Typography>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setSlotMenu(null);
            // Pre-fill with the first selected slot's current target
            const firstId = selectedSlotIds.values().next().value as string | undefined;
            const firstSlot = slotList.find((s) => s.id === firstId);
            setTargetValue(firstSlot?.target_headcount ?? 1);
            setTargetDialogOpen(true);
          }}
        >
          {t("planning.setTarget")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSlotMenu(null);
            setSelectedSlotIds(new Set());
          }}
        >
          {t("planning.clearSlotSelection")}
        </MenuItem>
      </Menu>

      <Dialog
        open={targetDialogOpen}
        onClose={() => setTargetDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("planning.targetDialogTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("planning.targetDialogHint", { count: selectedSlotIds.size })}
            </Typography>
            <TextField
              type="number"
              label={t("planning.targetLabel")}
              value={targetValue}
              onChange={(e) =>
                setTargetValue(
                  Math.max(1, Math.min(100, Number(e.target.value) || 1)),
                )
              }
              inputProps={{ min: 1, max: 100, step: 1 }}
              autoFocus
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTargetDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={slotTargetMutation.isPending}
            onClick={() =>
              slotTargetMutation.mutate({
                ids: Array.from(selectedSlotIds),
                target: targetValue,
              })
            }
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}


// ---------------------------------------------------------------------------
// Desktop grid: days in rows, slots in columns
// ---------------------------------------------------------------------------
type GridProps = {
  year: number;
  month: number;
  days: number[];
  slots: SlotTemplate[];
  assignmentsBySlot: Map<string, Assignment[]>;
  practitionerById: Map<string, Practitioner>;
  modeById: Map<string, ExerciseMode>;
  canEdit: boolean;
  organization?: Organization | null;
  onCellClick: (c: CellTarget) => void;
  onDelete: (id: string) => void;
  onHeaderRightClick?: (slotId: string, e: React.MouseEvent) => void;
  clipboardLabel?: string | null;
  selectedSlotIds?: Set<string>;
  onHeaderToggleSelect?: (slotId: string, additive: boolean) => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

function PlanningGridDesktop(props: GridProps) {
  const {
    year, month, days, slots, assignmentsBySlot, practitionerById, modeById,
    canEdit, organization, onCellClick, onHeaderRightClick,
    selectedSlotIds, onHeaderToggleSelect, clipboardLabel, t,
  } = props;

  return (
    <Paper
      variant="outlined"
      // Suppress the browser's own menu across the whole grid: right-click is
      // the planning's primary interaction here, and the native "Back /
      // Reload / Save as" menu on top of it is pure noise. Children that own a
      // menu call preventDefault themselves; this catches the gaps between them.
      onContextMenu={(e) => e.preventDefault()}
      sx={{
        overflow: "auto",
        maxHeight: "calc(100vh - 240px)",
        position: "relative",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `90px repeat(${slots.length}, minmax(0, 1fr))`,
          width: "100%",
          minWidth: 90 + slots.length * 64,
        }}
      >
        {/* Header — corner sticks both top & left */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: "background.paper",
            borderBottom: 1,
            borderColor: "divider",
            fontWeight: 600,
            position: "sticky",
            top: 0,
            left: 0,
            zIndex: 3,
          }}
        >
          {/* corner */}
        </Box>
        {slots.map((s) => {
          const isSelected = selectedSlotIds?.has(s.id) ?? false;
          // Count days OK / degraded / KO over the visible month, skipping
          // non-operating days so the slot summary reflects only days when
          // the organization is supposed to be staffed.
          const target = s.target_headcount;
          let okCount = 0;
          let degradedCount = 0;
          let koCount = 0;
          for (const d of days) {
            const date = ymd(year, month, d);
            if (!slotIsOperating(organization ?? null, date, s)) continue;
            const list = assignmentsBySlot.get(`${date}__${s.id}`) ?? [];
            if (list.length === 0) koCount += 1;
            else if (list.length < target) degradedCount += 1;
            else okCount += 1;
          }
          const total = okCount + degradedCount + koCount;
          const okPct = total > 0 ? (okCount / total) * 100 : 0;
          const degradedPct = total > 0 ? (degradedCount / total) * 100 : 0;
          const koPct = total > 0 ? (koCount / total) * 100 : 0;
          return (
            <Box
              key={s.id}
              onClick={(e) => {
                if (!canEdit) return;
                if (e.ctrlKey || e.metaKey || e.shiftKey) {
                  e.preventDefault();
                  onHeaderToggleSelect?.(s.id, true);
                }
              }}
              onContextMenu={(e) => {
                if (!canEdit) return;
                e.preventDefault();
                onHeaderRightClick?.(s.id, e);
              }}
              sx={{
                p: 1.5,
                bgcolor: isSelected ? "primary.light" : "background.paper",
                outline: isSelected ? "2px solid" : "none",
                outlineColor: "primary.main",
                outlineOffset: -2,
                borderBottom: 1,
                borderColor: "divider",
                borderLeft: 1,
                cursor: canEdit ? "context-menu" : "default",
                position: "sticky",
                top: 0,
                zIndex: 2,
              }}
              title={canEdit ? t("planning.headerHint") : ""}
            >
              <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
                <SlotKindChip kind={s.kind} />
                {target > 1 && (
                  <Tooltip title={t("planning.targetTooltip", { count: target })}>
                    <Chip
                      size="small"
                      variant="outlined"
                      color="info"
                      label={`× ${target}`}
                      sx={{ height: 18, fontSize: 10, "& .MuiChip-label": { px: 0.5 } }}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}
              </Typography>
              {total > 0 && target > 1 && (
                <Tooltip
                  title={t("planning.slotSummaryTooltip", {
                    ok: okCount,
                    degraded: degradedCount,
                    ko: koCount,
                    total,
                  })}
                  placement="top"
                >
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} alignItems="center">
                    <Box
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 2,
                        display: "flex",
                        overflow: "hidden",
                        bgcolor: "action.disabledBackground",
                      }}
                    >
                      <Box sx={{ width: `${okPct}%`, bgcolor: "success.main" }} />
                      <Box sx={{ width: `${degradedPct}%`, bgcolor: "warning.main" }} />
                      <Box sx={{ width: `${koPct}%`, bgcolor: "error.main" }} />
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: 9, lineHeight: 1 }}
                    >
                      {Math.round(okPct)}%
                    </Typography>
                  </Stack>
                </Tooltip>
              )}
            </Box>
          );
        })}

        {/* Rows — one row per day; the day content cell spans all slot columns
            and renders the per-slot conformity strip + one track per practitioner. */}
        {days.map((d) => {
          const date = ymd(year, month, d);
          const dow = dayOfWeek(year, month, d);
          const isWeekend = dow >= 6;
          return (
            <Box key={d} sx={{ display: "contents" }}>
              <Box
                sx={{
                  p: 1,
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: isWeekend ? "action.hover" : "background.paper",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 56,
                  position: "sticky",
                  left: 0,
                  zIndex: 1,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {d}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`planning.weekday.${dow}`)}
                </Typography>
                {(() => {
                  const cov = coverageForDate(date, slots, assignmentsBySlot, organization ?? null);
                  if (cov.totalHours === 0) return null;
                  return (
                    <Tooltip
                      title={t("planning.coverageTooltip", {
                        covered: cov.coveredHours,
                        total: cov.totalHours,
                        pct: cov.pct,
                      })}
                      placement="right"
                    >
                      <Chip
                        size="small"
                        color={coverageColor(cov.pct)}
                        variant="outlined"
                        label={`${cov.coveredHours}h/${cov.totalHours}h`}
                        sx={{ mt: 0.25, height: 16, fontSize: 10, "& .MuiChip-label": { px: 0.6 } }}
                      />
                    </Tooltip>
                  );
                })()}
              </Box>
              <PlanningDayTracks
                date={date}
                isWeekend={isWeekend}
                slots={slots}
                assignmentsBySlot={assignmentsBySlot}
                practitionerById={practitionerById}
                modeById={modeById}
                organization={organization ?? null}
                canEdit={canEdit}
                clipboardLabel={clipboardLabel}
                onAdd={(slot: SlotTemplate) => onCellClick({ date, slot })}
                onBlockAction={(action: BlockAction) => {
                  window.dispatchEvent(
                    new CustomEvent("planning-block-action", { detail: action }),
                  );
                }}
                t={t}
              />
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}


// ---------------------------------------------------------------------------
// Mobile grid: one accordion-like card per day
// ---------------------------------------------------------------------------
function PlanningGridMobile(props: GridProps) {
  const { year, month, days, slots, assignmentsBySlot, practitionerById, modeById, canEdit, organization, onCellClick, onDelete, t } = props;

  return (
    <Stack spacing={1.5}>
      {days.map((d) => {
        const date = ymd(year, month, d);
        const dow = dayOfWeek(year, month, d);
        const isWeekend = dow >= 6;
        return (
          <Paper
            key={d}
            variant="outlined"
            sx={{ p: 1.5, bgcolor: isWeekend ? "action.hover" : "transparent" }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {pad2(d)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(`planning.weekday.${dow}`)}
              </Typography>
              {isWeekend && <Chip size="small" variant="outlined" label={t("planning.weekend")} />}
              {(() => {
                const cov = coverageForDate(date, slots, assignmentsBySlot, organization ?? null);
                if (cov.totalHours === 0) return null;
                return (
                  <Tooltip
                    title={t("planning.coverageTooltip", {
                      covered: cov.coveredHours,
                      total: cov.totalHours,
                      pct: cov.pct,
                    })}
                  >
                    <Chip
                      size="small"
                      color={coverageColor(cov.pct)}
                      variant="outlined"
                      label={`${cov.coveredHours}h/${cov.totalHours}h`}
                    />
                  </Tooltip>
                );
              })()}
            </Stack>
            <Stack spacing={1}>
              {slots.map((s) => {
                const key = `${date}__${s.id}`;
                const list = assignmentsBySlot.get(key) ?? [];
                return (
                  <Box
                    key={s.id}
                    sx={{
                      p: 1,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                      <Box>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
                          <SlotKindChip kind={s.kind} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                          {s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}
                        </Typography>
                      </Box>
                      {canEdit && (
                        <IconButton size="small" onClick={() => onCellClick({ date, slot: s })}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    {list.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t("planning.emptyCell")}
                      </Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {list.map((a) => (
                          <AssignmentChip
                            key={a.id}
                            assignment={a}
                            practitioner={practitionerById.get(a.practitioner_id)}
                            mode={modeById.get(a.exercise_mode_id)}
                            canEdit={canEdit}
                            onDelete={() => onDelete(a.id)}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}


// ---------------------------------------------------------------------------
// Assignment chip
// ---------------------------------------------------------------------------

function AssignmentChip({
  assignment,
  practitioner,
  mode,
  canEdit,
  onDelete,
  reserveTopRight = false,
}: {
  assignment: Assignment;
  practitioner?: Practitioner;
  mode?: ExerciseMode;
  canEdit: boolean;
  onDelete: () => void;
  reserveTopRight?: boolean;
}) {
  const label = practitioner?.display_name ?? assignment.practitioner_id.slice(0, 6);
  const color = mode?.color ?? "#64748b";
  const skills = practitioner?.skill_codes ?? [];
  const tooltipBody = (
    <Box>
      <Box sx={{ fontWeight: 700 }}>{label}</Box>
      {mode?.code && <Box sx={{ opacity: 0.85 }}>Mode · {mode.code}</Box>}
      {skills.length > 0 && (
        <Box sx={{ mt: 0.5 }}>Compétences : {skills.join(", ")}</Box>
      )}
      {practitioner?.languages && practitioner.languages.length > 0 && (
        <Box>Langues : {practitioner.languages.map((l) => l.toUpperCase()).join(", ")}</Box>
      )}
    </Box>
  );
  return (
    <Tooltip title={tooltipBody} placement="top">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          bgcolor: color,
          color: "common.white",
          borderRadius: 0.75,
          px: 0.75,
          py: 0.25,
          fontSize: 12,
          lineHeight: 1.4,
          cursor: "default",
          /* keep space for the conformity chip on the first row of the cell */
          mr: reserveTopRight ? "38px" : 0,
          minWidth: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              minWidth: 0,
            }}
          >
            {label}
            {mode?.code && (
              <Box component="span" sx={{ opacity: 0.85, ml: 0.5, fontSize: 10 }}>
                · {mode.code}
              </Box>
            )}
          </Box>
          {skills.slice(0, 2).map((s) => (
            <Box
              key={s}
              component="span"
              sx={{
                bgcolor: "rgba(255,255,255,0.25)",
                color: "common.white",
                px: 0.5,
                borderRadius: 0.5,
                fontSize: 9,
                fontWeight: 700,
                lineHeight: 1.4,
                flexShrink: 0,
              }}
            >
              {s}
            </Box>
          ))}
          {skills.length > 2 && (
            <Box
              component="span"
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: 9,
                flexShrink: 0,
              }}
            >
              +{skills.length - 2}
            </Box>
          )}
        </Box>
        {canEdit && (
          <IconButton
            size="small"
            sx={{ color: "inherit", p: 0.25, flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
    </Tooltip>
  );
}
